import React, { useState, useEffect } from 'react';
import FormButton from '../common/FormButton';
import { authService } from '../../../services/api';

const UserPrivacy = ({ onNavigate }) => {
  const [formData, setFormData] = useState({
    show_email: false,
    show_phone: false,
    show_location: true,
    show_age: false
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    loadUserPrivacy();
  }, []);

  const loadUserPrivacy = async () => {
    setLoading(true);
    try {
      const privacy = await authService.getUserPrivacy();
      setFormData({
        show_email: privacy.show_email || false,
        show_phone: privacy.show_phone || false,
        show_location: privacy.show_location !== false,
        show_age: privacy.show_age || false
      });
    } catch (error) {
      setErrors({ general: error.message });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: checked
    }));
    // Clear error when user changes setting
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErrors({});
    setMessage('');
    
    try {
      await authService.updateUserPrivacy(formData);
      setMessage('Privacy settings updated successfully!');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      if (error.type === 'validation_error') {
        setErrors(error.errors);
      } else {
        setErrors({ general: error.message });
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-4 text-gray-600">Loading your privacy settings...</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="space-y-8">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">Privacy Settings</h2>
          <p className="text-gray-600">Control who can see your information</p>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          {errors.general && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
              {errors.general}
            </div>
          )}

          {message && (
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-md">
              {message}
            </div>
          )}

          <div className="space-y-6">
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Profile Visibility</h3>
              <p className="text-sm text-gray-600 mb-4">
                Control what information is visible to other users on your profile.
              </p>
              
              <div className="space-y-4">
                <label className="flex items-start">
                  <input
                    type="checkbox"
                    name="show_email"
                    checked={formData.show_email}
                    onChange={handleChange}
                    className="mt-1 mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <div>
                    <div className="text-sm font-medium text-gray-700">Show Email Address</div>
                    <div className="text-sm text-gray-500">
                      Allow other users to see your email address on your profile
                    </div>
                  </div>
                </label>

                <label className="flex items-start">
                  <input
                    type="checkbox"
                    name="show_phone"
                    checked={formData.show_phone}
                    onChange={handleChange}
                    className="mt-1 mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <div>
                    <div className="text-sm font-medium text-gray-700">Show Phone Number</div>
                    <div className="text-sm text-gray-500">
                      Allow other users to see your phone number on your profile
                    </div>
                  </div>
                </label>

                <label className="flex items-start">
                  <input
                    type="checkbox"
                    name="show_location"
                    checked={formData.show_location}
                    onChange={handleChange}
                    className="mt-1 mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <div>
                    <div className="text-sm font-medium text-gray-700">Show Location</div>
                    <div className="text-sm text-gray-500">
                      Allow other users to see your country and city on your profile
                    </div>
                  </div>
                </label>

                <label className="flex items-start">
                  <input
                    type="checkbox"
                    name="show_age"
                    checked={formData.show_age}
                    onChange={handleChange}
                    className="mt-1 mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <div>
                    <div className="text-sm font-medium text-gray-700">Show Age</div>
                    <div className="text-sm text-gray-500">
                      Allow other users to see your age on your profile
                    </div>
                  </div>
                </label>
              </div>
            </div>

            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-blue-800">Privacy Notice</h3>
                  <div className="mt-2 text-sm text-blue-700">
                    <p>
                      Your privacy is important to us. These settings control what information 
                      other users can see on your profile. You can change these settings at any time.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <FormButton
            type="submit"
            loading={saving}
            disabled={saving}
            className="w-full"
          >
            Update Privacy Settings
          </FormButton>
        </form>
      </div>
    </div>
  );
};

export default UserPrivacy;
